/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package models

import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.serialization.Serializable
import studio.PaymentFrequency

/**
 * Represents a user's savings goal
 */
@Serializable
data class SavingsGoal(
    val id: String,
    val name: String,
    val description: String = "",
    val targetAmount: Double,
    val currentAmount: Double = 0.0,
    val deadline: LocalDate,
    val category: GoalCategory,
    val priority: GoalPriority = GoalPriority.MEDIUM,
    val createdDate: Instant,
    val isActive: Boolean = true,
    val iconEmoji: String = "💰",
    // Kotlin/JS workaround: LocalDate doesn't serialize properly, so store string version
    val deadlineStr: String = "",
    /**
     * ISO date of the EARLIEST transaction assigned to this goal, or null when nothing
     * has been assigned yet. Server-computed.
     *
     * Pace needs it because [currentAmount] counts every assigned transaction, including
     * ones dated before the goal was created — you create a goal and then tag the
     * transfers you had already been making. Measuring elapsed time from [createdDate]
     * while counting that money would put progress the clock never covered in the
     * numerator, and every such goal reads as ahead of schedule.
     */
    val firstContributionDate: String? = null,
    // Wiz 3R AI fields — let the AI apply precise (vs conditional) frameworks.
    // All nullable so existing goals don't break; the AI uses conditional
    // phrasing when these are absent and prompts the user to set them.
    val accountType: GoalAccountType? = null,
    // DEBT_PAYOFF-specific fields. Only meaningful when category = DEBT_PAYOFF.
    val debtInterestRate: Double? = null,         // e.g. 4.85 for 4.85%
    val debtRemainingBalance: Double? = null,     // remaining loan principal
    val debtRemainingMonths: Int? = null,         // months left on the loan
    /** NEW: payment cadence the user committed to via "Apply to goal" in the
     *  Debt Studio. Null until the user applies a plan. */
    val debtPaymentFrequency: PaymentFrequency? = null,
    /** NEW: monthly-equivalent payment the user committed to via "Apply to
     *  goal" in the Debt Studio. Null until the user applies a plan. */
    val debtMonthlyEquivalentPayment: Double? = null,
)

/**
 * Progress tracking for a savings goal
 */
@Serializable
data class GoalProgress(
    val goalId: String,
    val currentSavings: Double,
    val percentageComplete: Double,
    val projectedCompletionDate: LocalDate?,
    val monthlyAverageContribution: Double,
    val isOnTrack: Boolean,
    val daysRemaining: Int,
    val recommendedMonthlyContribution: Double,
)

/**
 * AI-generated recommendation for achieving a goal
 */
@Serializable
data class SavingsRecommendation(
    val goalId: String,
    val spendingCategory: String,
    val currentMonthlySpending: Double,
    val recommendedMonthlySpending: Double,
    val suggestedReduction: Double,
    val rationale: String,
    val potentialSavings: Double,
    val confidenceScore: Double,
    val priority: Int,
)

/**
 * Comprehensive allocation plan for achieving goals
 */
@Serializable
data class GoalAllocationPlan(
    val goalId: String,
    val monthlyAllocationAmount: Double,
    val categoryAdjustments: List<CategoryAdjustment>,
    val projectedTimeline: TimelineProjection,
    val alternativeScenarios: List<AllocationScenario> = emptyList(),
)

/**
 * Suggested adjustment for a spending category
 */
@Serializable
data class CategoryAdjustment(
    val category: String,
    val currentSpending: Double,
    val recommendedSpending: Double,
    val reductionAmount: Double,
    val reductionPercentage: Double,
    val impact: String,
    val difficulty: AdjustmentDifficulty,
)

/**
 * Timeline projection for goal achievement
 */
@Serializable
data class TimelineProjection(
    val estimatedCompletionDate: LocalDate,
    val monthsRequired: Int,
    val confidenceLevel: Double,
    val assumptions: List<String>,
)

/**
 * Alternative allocation scenario
 */
@Serializable
data class AllocationScenario(
    val name: String,
    val monthlyContribution: Double,
    val completionDate: LocalDate,
    val tradeoffs: List<String>,
    val adjustments: List<CategoryAdjustment>,
)

/**
 * Goal categories
 */
enum class GoalCategory {
    EMERGENCY_FUND,
    VACATION,
    HOME_PURCHASE,
    RETIREMENT,
    EDUCATION,
    DEBT_PAYOFF,
    MAJOR_PURCHASE,
    INVESTMENT,
    OTHER,
}

/**
 * Goal priority levels for portfolio optimization
 */
enum class GoalPriority {
    MUST_HAVE, // Critical goals that must be funded first
    NICE_TO_HAVE, // Important but can be delayed if needed
    STRETCH_GOAL, // Aspirational goals with flexible timelines
    MAYBE, // Optional goals, lowest funding priority

    // Legacy values for backward compatibility
    @Deprecated("Use MUST_HAVE instead", ReplaceWith("MUST_HAVE"))
    CRITICAL,

    @Deprecated("Use NICE_TO_HAVE instead", ReplaceWith("NICE_TO_HAVE"))
    HIGH,

    @Deprecated("Use STRETCH_GOAL instead", ReplaceWith("STRETCH_GOAL"))
    MEDIUM,

    @Deprecated("Use MAYBE instead", ReplaceWith("MAYBE"))
    LOW,
}

/**
 * Difficulty level for category adjustments
 */
enum class AdjustmentDifficulty {
    EASY, // Less than 10% reduction
    MODERATE, // 10-25% reduction
    HARD, // 25-50% reduction
    EXTREME, // More than 50% reduction
}
